import { forwardRef, memo, Ref, useCallback, useEffect, useRef, useState } from 'react';
import ReactSwitch from 'react-switch';
import BreakIcon from '~/images/break-icon.svg';
import { Colors } from '~/themes/colors';
import { IOptionValue } from '../Autocomplete/interfaces';
import { Row } from '../Row';
import { Text } from '../Text';
import { Value } from './Field';
import { Radio } from './Radio';
import { IInputJobProps } from './Types';

const options: IOptionValue<string>[] = [
  { label: '0 min', value: '0 min' },
  { label: '15 min', value: '15 min' },
  { label: '30 min', value: '30 min' },
  { label: '45 min', value: '45 min' },
  { label: '1 hr', value: '1 hr' },
  { label: '1.5 hr', value: '1.5 hr' },
  { label: '2 hr', value: '2 hr' },
];

type TBreakChangeHandler = {
  (key: 'break', value: string): void;
  (key: 'is_paid', value: boolean): void;
};

/**
 * Props for the MealBreak component
 * @interface IMealBreakProps
 */
interface IMealBreakProps
  extends Omit<IInputJobProps<string>, 'defaultValue' | 'onChange' | 'value'> {
  /** Default break duration value for uncontrolled component */
  defaultBreak?: string;
  /** Default payment status for uncontrolled component */
  defaultIsPaid?: boolean;
  /** Current break duration value for controlled component */
  valueBreak?: string;
  /** Current payment status for controlled component */
  valueIsPaid?: boolean;
  /** Callback fired when break duration or status changes */
  onChange?: TBreakChangeHandler;
}

const Component = (
  {
    defaultBreak,
    defaultIsPaid,
    isDisabled,
    isOpen,
    onChange,
    onToggle,
    valueBreak,
    valueIsPaid: controlledIsPaid,
  }: IMealBreakProps,
  ref: Ref<HTMLDivElement>,
) => {
  const [internalIsPaidValue, setInternalIsPaidValue] = useState(defaultIsPaid);

  const isIsPaidControlled = controlledIsPaid !== undefined;
  const currentIsPaidValue = isIsPaidControlled
    ? (controlledIsPaid ?? false)
    : (internalIsPaidValue ?? false);

  const wasControlledRef = useRef(isIsPaidControlled);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && wasControlledRef.current !== isIsPaidControlled) {
      console.warn(
        `Warning: MealBreak (isPaid state) component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isIsPaidControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isIsPaidControlled;
  }, [isIsPaidControlled]);

  const handleBreakValueChange = useCallback(
    (newValue: string) => {
      onChange?.('break', newValue);
    },
    [onChange],
  );

  const handleIsPaidValueChange = useCallback(
    (newValue: boolean) => {
      if (!isIsPaidControlled) {
        setInternalIsPaidValue(newValue);
      }
      onChange?.('is_paid', newValue);
    },
    [isIsPaidControlled, onChange],
  );

  const CustomValueComponent = useCallback(
    (currentValue: string | undefined) => {
      let displayText = 'Time';

      const color = isOpen || !currentValue ? Colors.neutral_300 : Colors.neutral_600;

      if (!isOpen && currentValue) {
        displayText = currentValue
          ? `${currentValue} (${currentIsPaidValue ? 'Paid' : 'Unpaid'})`
          : 'Time';
      }

      return (
        <Value
          text={displayText}
          style={{
            color,
          }}
        />
      );
    },
    [currentIsPaidValue, isOpen],
  );

  return (
    <Radio
      IconSrc={BreakIcon}
      ValueComponent={CustomValueComponent}
      autoClose={false}
      defaultValue={defaultBreak}
      isDisabled={isDisabled}
      isOpen={isOpen}
      onChange={handleBreakValueChange}
      onToggle={onToggle}
      options={options}
      ref={ref}
      testName="input-job.meal-break"
      title="Meal Break:"
      value={valueBreak}
    >
      <Row
        alignItems="center"
        data-cy={`input-job.meal-break.paid-switch`}
        data-testid={`input-job.meal-break.paid-switch`}
        gap={10}
        marginRight={32}
        marginTop={16}
      >
        <ReactSwitch
          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
          checked={currentIsPaidValue}
          checkedIcon={false}
          className="react-switch"
          handleDiameter={20}
          height={25}
          id="material-switch"
          offColor={Colors.neutral_100}
          onChange={handleIsPaidValueChange}
          onColor={Colors.secondary_500}
          onHandleColor={Colors.white}
          uncheckedIcon={false}
          width={50}
        />
        <Text bold>Meal Break is Paid</Text>
      </Row>
    </Radio>
  );
};

/**
 * A composite input component for configuring meal break duration and payment status.
 *
 * This component allows users to select a meal break duration from predefined options
 * and toggle whether the break is paid or unpaid. It supports both controlled and
 * uncontrolled usage patterns.
 *
 * @example
 * ```tsx
 * // Uncontrolled usage
 * <MealBreak
 *   defaultBreak="30 min"
 *   defaultIsPaid={true}
 *   onChange={(key, value) => {
 *     if (key === 'break') console.log('Break duration:', value);
 *     if (key === 'is_paid') console.log('Is paid:', value);
 *   }}
 * />
 *
 * // Controlled usage
 * <MealBreak
 *   valueBreak={breakDuration}
 *   valueIsPaid={isPaid}
 *   onChange={(key, value) => {
 *     if (key === 'break') setBreakDuration(value);
 *     if (key === 'is_paid') setIsPaid(value);
 *   }}
 * />
 * ```
 *
 * @public
 */
export const MealBreak = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IMealBreakProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
